<template>
  <div>
    <v-card-title class="body-2 v-card__subtitle">
      <v-icon small class="mr-2">mdi-chart-scatter-plot</v-icon>
      Leistungsverteilung (Scatter)
      <v-spacer></v-spacer>
      <v-chip small outlined text>
        <v-icon small left>mdi-cursor-pointer</v-icon>
        Klicken für Details
      </v-chip>
    </v-card-title>
    <v-card-subtitle class="py-1">
      <v-select
        v-model="selectedTarget"
        :items="targetOptions"
        item-text="text"
        item-value="value"
        label="Zielvorgabe"
        dense
        outlined
        hide-details
        class="text-caption"
        style="max-width: 400px"
      ></v-select>
    </v-card-subtitle>
    <v-card-text v-if="!loading && hasData">
      <VueApexCharts
        type="scatter"
        :options="chartOptions"
        :series="chartSeries"
        :height="height"
      ></VueApexCharts>
    </v-card-text>
    <v-card-text
      v-else-if="loading"
      class="d-flex justify-center align-center"
      :style="{ height: height + 'px' }"
    >
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-card-text>
    <v-card-text
      v-else
      class="d-flex justify-center align-center"
      :style="{ height: height + 'px' }"
    >
      <div class="text-center grey--text">
        <v-icon large color="grey">mdi-chart-scatter-plot</v-icon>
        <div class="mt-2">Keine Daten verfügbar</div>
      </div>
    </v-card-text>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import apexChartsDarkMode from "@/mixins/apexChartsDarkMode";

export default {
  name: "PerformanceScatterChart",
  mixins: [apexChartsDarkMode],
  components: {
    VueApexCharts,
  },
  props: {
    targets: {
      type: Array,
      required: true,
    },
    performanceData: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: 500,
    },
  },
  data() {
    return {
      selectedTarget: null,
      groupRanges: {},
    };
  },
  mounted() {
    // Pre-select first target
    if (this.targets.length > 0) {
      this.selectedTarget = this.targets[0].meta.id;
    }
  },
  computed: {
    targetOptions() {
      const options = [];

      this.targets.forEach((target) => {
        options.push({
          text: target.shortTitle || target.title,
          value: target.meta.id,
        });
      });

      return options;
    },
    hasData() {
      return this.performanceData.length > 0 && this.selectedTarget !== null;
    },
    chartSeries() {
      if (!this.selectedTarget) return [];

      const greenMembers = [];
      const yellowMembers = [];
      const redMembers = [];

      // Sort members by group sortKey, then by name within each group
      const sortedMembers = [...this.performanceData].sort((a, b) => {
        if (a.group.sortKey !== b.group.sortKey) {
          return a.group.sortKey - b.group.sortKey;
        }
        return a.user.displayName.localeCompare(b.user.displayName);
      });

      let sequentialIndex = 0;
      const groupRanges = {}; // Track x-position ranges for each group

      sortedMembers.forEach((member) => {
        const result = member.performanceTargetResults[this.selectedTarget];
        if (
          !result ||
          result.status === "notApplicable" ||
          result.status === "fullySuspended"
        ) {
          return;
        }

        const target = this.targets.find(
          (t) => t.meta.id === this.selectedTarget
        );
        if (!target) return;

        sequentialIndex++;

        // Track group ranges for annotations
        const groupTitle = member.group.title;
        if (!groupRanges[groupTitle]) {
          groupRanges[groupTitle] = {
            start: sequentialIndex,
            end: sequentialIndex,
          };
        } else {
          groupRanges[groupTitle].end = sequentialIndex;
        }

        const istWert = parseFloat(result.value) || 0;
        const sollWert = parseFloat(target.targetValue) || 0;
        const unit = target.unit || "";
        const status = result.status === "met" ? "Erfüllt" : "Nicht erfüllt";

        const dataPoint = {
          x: sequentialIndex,
          y: istWert,
          name: member.user.displayName,
          group: member.group.title,
          info: `${status}: ${istWert}${unit ? " " + unit : ""} / ${sollWert}${
            unit ? " " + unit : ""
          }`,
          uid: member.user.uid,
          memberData: member,
          targetValue: sollWert,
          actualValue: istWert,
          status: result.status,
          unit: unit,
        };

        if (member.trafficLightStatus === "green") {
          greenMembers.push(dataPoint);
        } else if (member.trafficLightStatus === "yellow") {
          yellowMembers.push(dataPoint);
        } else {
          redMembers.push(dataPoint);
        }
      });

      // Update reactive data property instead of direct assignment
      this.$nextTick(() => {
        this.groupRanges = groupRanges;
      });

      return [
        {
          name: "Grün",
          data: greenMembers,
        },
        {
          name: "Gelb",
          data: yellowMembers,
        },
        {
          name: "Rot",
          data: redMembers,
        },
      ];
    },
    chartOptions() {
      const vm = this;

      if (!this.selectedTarget) {
        return {};
      }

      // Calculate actual data range for Y-axis (Ist-Wert)
      const allYValues = this.chartSeries.flatMap((series) =>
        series.data.map((d) => d.y)
      );

      const minY = allYValues.length > 0 ? Math.min(...allYValues) : 0;
      const maxY = allYValues.length > 0 ? Math.max(...allYValues) : 100;

      // Add padding for better visualization
      const yRange = maxY - minY;
      const yPadding = Math.max(5, yRange * 0.15);

      const adjustedMinY = Math.max(0, Math.floor(minY - yPadding));
      const adjustedMaxY = Math.ceil(maxY + yPadding);

      // Get target info for reference line
      const selectedTargetObj = this.targets.find(
        (t) => t.meta.id === this.selectedTarget
      );
      const targetValue = selectedTargetObj
        ? parseFloat(selectedTargetObj.targetValue.value) || 0
        : 0;
      const unit = selectedTargetObj?.targetValue.unit || "";

      // Create x-axis annotations for group labels
      const xAxisAnnotations = [];
      if (this.groupRanges) {
        Object.entries(this.groupRanges).forEach(([groupTitle, range]) => {
          // const midPoint = (range.start + range.end) / 2;
          xAxisAnnotations.push({
            x: range.start - 0.5,
            borderColor: "#999",
            label: {
              borderColor: "#999",
              style: {
                color: "#fff",
                background: "#999",
                fontSize: "10px",
              },
              text: groupTitle,
              // orientation: "horizontal",
            },
          });
        });
      }

      return {
        ...this.apexChartsDarkModeOptions,
        chart: {
          ...this.apexChartsDarkModeOptions.chart,
          type: "scatter",
          toolbar: {
            show: true,
          },
          zoom: {
            enabled: true,
          },
          events: {
            dataPointSelection: function (event, chartContext, config) {
              vm.handlePointClick(config);
            },
          },
        },
        colors: ["#4CAF50", "#FF9800", "#F44336"],
        xaxis: {
          title: {
            text: "Mitglieder (sequenziell)",
          },
          min: 0,
          labels: {
            show: false,
          },
          axisBorder: {
            show: true,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          title: {
            text: unit ? `Ist-Wert (${unit})` : "Ist-Wert",
          },
          min: adjustedMinY,
          max: adjustedMaxY,
          labels: {
            formatter: function (val) {
              return Math.round(val);
            },
          },
        },
        annotations: {
          yaxis: [
            {
              y: targetValue,
              borderColor: "#775DD0",
              strokeDashArray: 5,
              label: {
                borderColor: "#775DD0",
                style: {
                  color: "#fff",
                  background: "#775DD0",
                },
                text: `Soll-Wert: ${targetValue}${unit ? " " + unit : ""}`,
              },
            },
          ],
          xaxis: xAxisAnnotations,
        },
        markers: {
          size: 6,
          hover: {
            size: 8,
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
        },
        tooltip: {
          custom: function ({ seriesIndex, dataPointIndex, w }) {
            const data = w.config.series[seriesIndex].data[dataPointIndex];
            const unit = data.unit ? " " + data.unit : "";

            return (
              '<div class="apexcharts-tooltip-custom pa-2">' +
              '<div class="font-weight-bold">' +
              data.name +
              "</div>" +
              '<div class="text-caption">Gruppe: ' +
              data.group +
              "</div>" +
              '<div class="text-caption">Ist-Wert: ' +
              data.actualValue +
              unit +
              "</div>" +
              '<div class="text-caption">Soll-Wert: ' +
              data.targetValue +
              unit +
              "</div>" +
              '<div class="text-caption font-weight-bold">' +
              data.info +
              "</div>" +
              "</div>"
            );
          },
        },
        grid: {
          borderColor: "#e0e0e0",
          strokeDashArray: 4,
          xaxis: {
            lines: {
              show: true,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
      };
    },
  },
  methods: {
    handlePointClick(config) {
      const { seriesIndex, dataPointIndex } = config;
      const dataPoint = this.chartSeries[seriesIndex].data[dataPointIndex];

      if (!dataPoint || !dataPoint.memberData) return;

      const member = dataPoint.memberData;
      const target = this.targets.find(
        (t) => t.meta.id === this.selectedTarget
      );
      const result = member.performanceTargetResults[this.selectedTarget];

      if (!target || !result) return;

      const unit = target.unit || "";

      // Build items for sidebar
      const items = [
        {
          title: "Ist-Wert",
          subtitle: "Tatsächlich erreichter Wert",
          value: `${result.value || "N/A"}${unit ? " " + unit : ""}`,
        },
        {
          title: "Soll-Wert",
          subtitle: "Zielvorgabe",
          value: `${target.targetValue || "N/A"}${unit ? " " + unit : ""}`,
        },
        {
          title: "Differenz",
          subtitle:
            dataPoint.actualValue >= dataPoint.targetValue
              ? "Über Soll"
              : "Unter Soll",
          value: `${Math.abs(dataPoint.actualValue - dataPoint.targetValue)}${
            unit ? " " + unit : ""
          }`,
          valueColor:
            dataPoint.actualValue >= dataPoint.targetValue
              ? "success"
              : "error",
        },
        {
          title: "Status",
          subtitle:
            result.status === "met" ? "Ziel erfüllt" : "Ziel nicht erfüllt",
          value: result.status === "met" ? "✓" : "✗",
          valueColor: result.status === "met" ? "success" : "error",
        },
      ];

      this.$emit("point-selected", {
        type: "list",
        chartContext: {
          label: "Leistungsverteilung (Scatter)",
          icon: "mdi-chart-scatter-plot",
        },
        title: member.user.displayName,
        subtitle: `${member.group.title} • ${
          target.shortTitle || target.title
        }`,
        icon:
          member.trafficLightStatus === "green"
            ? "mdi-check-circle"
            : member.trafficLightStatus === "yellow"
            ? "mdi-alert-circle"
            : "mdi-close-circle",
        iconColor:
          member.trafficLightStatus === "green"
            ? "success"
            : member.trafficLightStatus === "yellow"
            ? "warning"
            : "error",
        count: items.length,
        countLabel: "Details",
        items: items,
      });
    },
  },
};
</script>
